import getEnvVar from '@/utils/getEnvVar';
import axios, { AxiosResponse } from 'axios';
import { retry } from './retry';
import { TranslatedQuery } from '@/types/download/TranslatedQuery';
import { MergeValues } from '@/types/download/DownloadButtonProps';
import { validateDownloadQuery } from './validateDownloadQuery';
import { DownloadRequestError } from './DownloadRequestError';

interface DownloadDataParams {
  bearerToken: string;
  fileNamePrefix: string;
  translatedQuery: TranslatedQuery;
  mergeValues?: MergeValues;
}

export const GATEWAY_TIMEOUT_STATUS = 504;

// An oversized download trips API Gateway's 30s timeout: it arrives as a 504 response, or — when the gateway's CORS headers are stripped — as a response-less network error. Both mean "too large".
export const isGatewayTimeoutAxiosError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.response) return error.response.status === GATEWAY_TIMEOUT_STATUS;
  return error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
};

async function getDownloadUrl(downloadDataParams: DownloadDataParams): Promise<string | undefined> {
  const { bearerToken, translatedQuery, fileNamePrefix, mergeValues } = downloadDataParams;

  validateDownloadQuery(translatedQuery.sql);

  const devEndpoint = getEnvVar('VITE_DATA_API_URL');

  const response: AxiosResponse = await axios.post(
    `${devEndpoint}/download/data`,
    {
      query: translatedQuery.sql,
      values: translatedQuery.values,
      token: bearerToken,
      fileNamePrefix,
      ...(mergeValues ? { mergeValues } : {}),
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${bearerToken}`,
      },
    },
  );

  if (response.status !== 200) {
    throw new Error(`Unexpected response status: ${response.status}`);
  }
  return response.data?.file_signed_url;
}

export const downloadData = async (downloadDataParams: DownloadDataParams): Promise<string | undefined> => {
  const maxRetries = 3;
  try {
    return await retry(
      () => getDownloadUrl(downloadDataParams),
      maxRetries,
      error => !isGatewayTimeoutAxiosError(error),
    );
  } catch (error) {
    if (isGatewayTimeoutAxiosError(error)) {
      throw new DownloadRequestError('Download timed out at the gateway (list too large).', GATEWAY_TIMEOUT_STATUS);
    }
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const detail = error instanceof Error ? error.message : String(error);
    throw new DownloadRequestError(`Download failed after ${maxRetries} attempts: ${detail}`, status);
  }
};
